import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { GoogleMaps } from "@/components/dashboard/google-maps";
import { Link } from "wouter";
import { 
  Globe, Shield, AlertTriangle, TrendingUp, MapPin, 
  Search, Filter, Download, Eye, ExternalLink,
  Activity, Zap, Target, Users, Clock, Wifi,
  Database, Network, Lock, CreditCard
} from "lucide-react";

interface ThreatData {
  id: string;
  type: 'malware' | 'phishing' | 'botnet' | 'ddos' | 'vulnerability' | 'suspicious' | 'spam';
  severity: 'critical' | 'high' | 'medium' | 'low';
  sourceIp: string;
  country: string;
  region: string;
  city: string;
  timestamp: string;
  description: string;
  targetDomains: string[];
  attackVector: string;
  confidence: number;
  abuseConfidence?: number;
  isp?: string;
  reports?: number;
  virusTotalScore?: number;
  urlScanVerdict?: string;
  detectionSource: 'AbuseIPDB' | 'VirusTotal' | 'URLScan.io' | 'Multiple' | 'System';
  additionalInfo?: any;
}

interface GlobalStats {
  totalThreats: number;
  activeCampaigns: number;
  blockedAttacks: number;
  affectedCountries: number;
  lastUpdated: string;
  sourceBreakdown: {
    abuseIPDB: number;
    virusTotal: number;
    urlScan: number;
    multiple: number;
  };
  topThreatTypes?: { type: string; count: number }[];
  regionalData?: { region: string; threats: number; severity: string }[];
}

// Real threat intelligence data from API
const getThreatData = (threatData: any): ThreatData[] => {
  if (!threatData?.threats || !Array.isArray(threatData.threats)) {
    return [];
  }
  
  return threatData.threats.map((threat: any, index: number) => ({
    id: threat.id || `threat-${index + 1}`,
    type: threat.type || 'malware',
    severity: threat.severity || 'medium',
    sourceIp: threat.sourceIp || 'Unknown',
    country: threat.country || 'Unknown',
    region: threat.region || 'Unknown',
    city: threat.city || 'Unknown',
    timestamp: threat.timestamp || new Date().toISOString(),
    description: threat.description || 'Security threat detected',
    targetDomains: threat.targetDomains || [],
    attackVector: threat.attackVector || 'Unknown',
    confidence: threat.confidence || 75,
    detectionSource: threat.detectionSource || 'Multiple'
  }));
};

export default function ThreatIntelligence() {
  const [searchTerm, setSearchTerm] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [selectedThreat, setSelectedThreat] = useState<ThreatData | null>(null);
  const [liveFeedActive, setLiveFeedActive] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(new Date());

  // Get subscription status to check if user has paid plan
  const { data: subscriptionStatus } = useQuery({
    queryKey: ["/api/subscription/status"],
  });

  // Check if user is blocked (trial/free user)
  const isTrialBlocked = subscriptionStatus?.isTrial && !subscriptionStatus?.hasActiveSubscription;

  // Fetch threat intelligence data from API with live updates
  const { data: threatData, isLoading, error } = useQuery<{
    threats: ThreatData[];
    globalStats: GlobalStats;
    regionalData: { region: string; threats: number; severity: string }[];
  }>({
    queryKey: ["/api/threat-intelligence"],
    refetchInterval: liveFeedActive ? 5000 : 30000, // 5 seconds when live, 30 seconds otherwise
    enabled: !isTrialBlocked, // Don't fetch if trial user
    retry: false,
  });

  // Real-time updates effect with enhanced threat detection
  useEffect(() => {
    if (threatData) {
      setLastUpdate(new Date());
      console.log('🛡️ Live Threat Detection Update:', {
        threats: threatData.threats?.length || 0,
        globalStats: threatData.globalStats,
        timestamp: new Date().toISOString()
      });
    }
  }, [threatData]);

  // Live feed pulse animation
  useEffect(() => {
    const interval = setInterval(() => {
      setLiveFeedActive(prev => !prev);
    }, 2000);
    return () => clearInterval(interval);
  }, []);

  const threats = threatData?.threats || [];
  const globalStats = threatData?.globalStats || {
    totalThreats: 0,
    activeCampaigns: 0,
    blockedAttacks: 0,
    affectedCountries: 0,
    lastUpdated: new Date().toISOString(),
    sourceBreakdown: {
      abuseIPDB: 0,
      virusTotal: 0,
      urlScan: 0,
      multiple: 0
    },
    topThreatTypes: [],
    regionalData: []
  };
  const regionalData = threatData?.regionalData || [];

  // Calculate dynamic threat type statistics from actual data
  const topThreatTypes = threats.length > 0 ? Object.entries(
    threats.reduce((acc, t) => ({ ...acc, [t.type]: (acc[t.type] || 0) + 1 }), {} as Record<string, number>)
  ).map(([type, count]) => ({ type, count })).sort((a, b) => b.count - a.count) : [];

  // Show upgrade prompt for trial users
  if (isTrialBlocked || error || (subscriptionStatus?.isTrial && !subscriptionStatus?.hasActiveSubscription)) {
    return (
      <div className="space-y-6 w-full max-w-none">
        <Card className="border-2 border-yellow-500">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-yellow-600">
              <Lock className="h-5 w-5" />
              Paid Feature - Upgrade Required
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-slate-600 dark:text-slate-400">
              Threat Intelligence is a premium feature available only for paid subscribers. 
              Trial users can scan domains manually, but advanced threat intelligence scanning requires a subscription.
            </p>
            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
              <h3 className="font-semibold mb-2">Upgrade to unlock:</h3>
              <ul className="list-disc list-inside space-y-1 text-sm text-slate-600 dark:text-slate-400">
                <li>Global threat intelligence scanning</li>
                <li>Real-time threat detection from multiple sources (AbuseIPDB, VirusTotal, URLScan.io)</li>
                <li>Geographic threat mapping and visualization</li>
                <li>Advanced threat analysis and reporting</li>
                <li>Export threat intelligence reports</li>
                <li>Priority threat alerts</li>
              </ul>
            </div>
            <Link href="/subscription">
              <Button className="w-full sm:w-auto">
                <CreditCard className="h-4 w-4 mr-2" />
                Upgrade to Paid Plan
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  const filteredThreats = threats.filter(threat => {
    const matchesSearch = 
      threat.sourceIp.includes(searchTerm) ||
      threat.country.toLowerCase().includes(searchTerm.toLowerCase()) ||
      threat.description.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filterType === "all" || threat.type === filterType;
    return matchesSearch && matchesFilter;
  });

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case 'critical': return 'bg-red-100 text-red-800 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'low': return 'bg-green-100 text-green-800 border-green-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getThreatIcon = (type: string) => {
    switch (type) {
      case 'malware': return <Zap className="h-4 w-4 text-red-500" />;
      case 'phishing': return <Target className="h-4 w-4 text-orange-500" />;
      case 'botnet': return <Wifi className="h-4 w-4 text-purple-500" />;
      case 'ddos': return <Activity className="h-4 w-4 text-blue-500" />;
      case 'vulnerability': return <Shield className="h-4 w-4 text-yellow-500" />;
      case 'suspicious': return <AlertTriangle className="h-4 w-4 text-amber-500" />;
      case 'spam': return <Activity className="h-4 w-4 text-pink-500" />;
      default: return <AlertTriangle className="h-4 w-4 text-gray-500" />;
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
            Global Threat Intelligence
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Real-time security threats and attack patterns worldwide
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={() => {
              const link = document.createElement('a');
              link.href = '/api/export/threat-intelligence';
              link.download = `threat-intelligence-${new Date().toISOString().split('T')[0]}.csv`;
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            <Download className="h-4 w-4 mr-2" />
            Export Report
          </Button>
          <Badge 
            variant="secondary" 
            className={`text-xs transition-all duration-300 ${liveFeedActive ? 'bg-emerald-100 text-emerald-800 border-emerald-300' : 'bg-slate-100 text-slate-600'}`}
          >
            <div className={`w-2 h-2 rounded-full mr-2 ${liveFeedActive ? 'bg-purple-500 animate-pulse' : 'bg-slate-400'}`}></div>
            Live Feed {liveFeedActive ? 'Active' : 'Standby'} • {threats.length} Threats
          </Badge>
          <span className="text-xs text-slate-500">
            Last update: {lastUpdate.toLocaleTimeString()}
          </span>
        </div>
      </div>

      {/* Global Statistics - Live Animated */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="transform transition-all duration-300 hover:scale-105 hover:shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className={`p-2 bg-red-100 rounded-lg transition-all duration-500 ${liveFeedActive ? 'animate-pulse bg-red-200' : ''}`}>
                <AlertTriangle className={`h-6 w-6 text-red-600 transition-transform duration-300 ${liveFeedActive ? 'animate-bounce' : ''}`} />
              </div>
              <div>
                <div className={`text-2xl font-bold text-red-600 transition-all duration-300 ${liveFeedActive ? 'scale-110' : ''}`}>
                  {globalStats.totalThreats.toLocaleString()}
                </div>
                <div className="text-sm text-slate-600">Active Threats</div>
                {liveFeedActive && (
                  <div className="flex items-center mt-1">
                    <div className="w-2 h-2 bg-red-500 rounded-full animate-ping mr-1"></div>
                    <span className="text-xs text-red-500">Live</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="transform transition-all duration-300 hover:scale-105 hover:shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className={`p-2 bg-orange-100 rounded-lg transition-all duration-500 ${liveFeedActive ? 'animate-pulse bg-orange-200' : ''}`}>
                <Target className={`h-6 w-6 text-orange-600 transition-transform duration-300 ${liveFeedActive ? 'animate-spin' : ''}`} />
              </div>
              <div>
                <div className={`text-2xl font-bold text-orange-600 transition-all duration-300 ${liveFeedActive ? 'scale-110' : ''}`}>
                  {globalStats.activeCampaigns}
                </div>
                <div className="text-sm text-slate-600">Active Campaigns</div>
                {liveFeedActive && (
                  <div className="flex items-center mt-1">
                    <div className="w-2 h-2 bg-orange-500 rounded-full animate-ping mr-1"></div>
                    <span className="text-xs text-orange-500">Live</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="transform transition-all duration-300 hover:scale-105 hover:shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className={`p-2 bg-green-100 rounded-lg transition-all duration-500 ${liveFeedActive ? 'animate-pulse bg-green-200' : ''}`}>
                <Shield className={`h-6 w-6 text-purple-600 transition-transform duration-300 ${liveFeedActive ? 'animate-pulse' : ''}`} />
              </div>
              <div>
                <div className={`text-2xl font-bold text-purple-600 transition-all duration-300 ${liveFeedActive ? 'scale-110' : ''}`}>
                  {globalStats.blockedAttacks.toLocaleString()}
                </div>
                <div className="text-sm text-slate-600">Blocked Attacks</div>
                {liveFeedActive && (
                  <div className="flex items-center mt-1">
                    <div className="w-2 h-2 bg-purple-500 rounded-full animate-ping mr-1"></div>
                    <span className="text-xs text-purple-500">Live</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="transform transition-all duration-300 hover:scale-105 hover:shadow-lg">
          <CardContent className="p-6">
            <div className="flex items-center gap-3">
              <div className={`p-2 bg-blue-100 rounded-lg transition-all duration-500 ${liveFeedActive ? 'animate-pulse bg-blue-200' : ''}`}>
                <Globe className={`h-6 w-6 text-blue-600 transition-transform duration-300 ${liveFeedActive ? 'animate-spin' : ''}`} />
              </div>
              <div>
                <div className={`text-2xl font-bold text-blue-600 transition-all duration-300 ${liveFeedActive ? 'scale-110' : ''}`}>
                  {globalStats.affectedCountries}
                </div>
                <div className="text-sm text-slate-600">Countries Affected</div>
                {liveFeedActive && (
                  <div className="flex items-center mt-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-ping mr-1"></div>
                    <span className="text-xs text-blue-500">Live</span>
                  </div>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Enhanced Multisource Threat Intelligence */}
      <Card className="bg-gradient-to-r from-purple-50 to-blue-50 dark:from-emerald-950/20 dark:to-blue-950/20">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-purple-600" />
            Multisource Threat Intelligence Platform
            <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse ml-2"></div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white dark:bg-slate-800 p-4 rounded-lg border border-red-200 relative">
              <div className="flex items-center gap-2 mb-2">
                <Database className="h-5 w-5 text-red-600" />
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">AbuseIPDB</span>
                <div className={`w-2 h-2 rounded-full ${globalStats.sourceBreakdown?.abuseIPDB > 0 ? 'bg-purple-500 animate-pulse' : 'bg-gray-400'}`}></div>
              </div>
              <div className="text-xl font-bold text-red-600 mb-1">{globalStats.sourceBreakdown?.abuseIPDB || 0}</div>
              <div className="text-xs text-slate-500">IP reputation & abuse reports</div>
              <div className="text-xs text-purple-600 mt-1">✓ Real-time monitoring</div>
            </div>
            
            <div className="bg-white dark:bg-slate-800 p-4 rounded-lg border border-blue-200 relative">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-5 w-5 text-blue-600" />
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">VirusTotal</span>
                <div className={`w-2 h-2 rounded-full ${globalStats.sourceBreakdown?.virusTotal > 0 ? 'bg-purple-500 animate-pulse' : 'bg-yellow-400'}`}></div>
              </div>
              <div className="text-xl font-bold text-blue-600 mb-1">{globalStats.sourceBreakdown?.virusTotal || 0}</div>
              <div className="text-xs text-slate-500">Domain & URL analysis</div>
              <div className="text-xs text-blue-600 mt-1">✓ 70+ security vendors</div>
            </div>
            
            <div className="bg-white dark:bg-slate-800 p-4 rounded-lg border border-green-200 relative">
              <div className="flex items-center gap-2 mb-2">
                <Search className="h-5 w-5 text-purple-600" />
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">URLScan.io</span>
                <div className={`w-2 h-2 rounded-full ${globalStats.sourceBreakdown?.urlScan > 0 ? 'bg-purple-500 animate-pulse' : 'bg-gray-400'}`}></div>
              </div>
              <div className="text-xl font-bold text-purple-600 mb-1">{globalStats.sourceBreakdown?.urlScan || 0}</div>
              <div className="text-xs text-slate-500">Website behavior analysis</div>
              <div className="text-xs text-purple-600 mt-1">✓ Sandbox scanning</div>
            </div>
            
            <div className="bg-white dark:bg-slate-800 p-4 rounded-lg border border-purple-200 relative">
              <div className="flex items-center gap-2 mb-2">
                <Network className="h-5 w-5 text-purple-600" />
                <span className="text-sm font-medium text-slate-600 dark:text-slate-400">Pattern Analysis</span>
                <div className={`w-2 h-2 rounded-full ${globalStats.sourceBreakdown?.multiple > 0 ? 'bg-purple-500 animate-pulse' : 'bg-purple-500'}`}></div>
              </div>
              <div className="text-xl font-bold text-purple-600 mb-1">{globalStats.sourceBreakdown?.multiple || 0}</div>
              <div className="text-xs text-slate-500">Script entropy & behavioral</div>
              <div className="text-xs text-purple-600 mt-1">✓ AI-powered detection</div>
            </div>
          </div>
          
          <div className="mt-4 space-y-2">
            <div className="p-3 bg-purple-50 dark:bg-purple-900/20 rounded-lg border border-emerald-200">
              <div className="flex items-center gap-2 text-sm text-emerald-700 dark:text-emerald-300">
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                <span className="font-medium">Live Intelligence Feed:</span>
                <span>Aggregating threat data from multiple security vendors and behavioral analysis engines</span>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2 text-xs">
              <div className="flex items-center gap-1 px-2 py-1 bg-blue-100 dark:bg-blue-900/30 rounded">
                <div className={`w-2 h-2 rounded-full ${
                  threatData?.globalStats?.sourceBreakdown?.virusTotal > 0 
                    ? 'bg-purple-500 animate-pulse' 
                    : 'bg-yellow-400'
                }`}></div>
                <span>VirusTotal API</span>
                <span className={threatData?.globalStats?.sourceBreakdown?.virusTotal > 0 
                  ? "text-purple-600" 
                  : "text-yellow-600"
                }>
                  {threatData?.globalStats?.sourceBreakdown?.virusTotal > 0 
                    ? 'Active' 
                    : 'Ready for Configuration'}
                </span>
              </div>
              <div className="flex items-center gap-1 px-2 py-1 bg-red-100 dark:bg-red-900/30 rounded">
                <div className={`w-2 h-2 rounded-full ${
                  threatData?.globalStats?.sourceBreakdown?.abuseIPDB > 0 
                    ? 'bg-purple-500 animate-pulse' 
                    : 'bg-gray-400'
                }`}></div>
                <span>AbuseIPDB</span>
                <span className={threatData?.globalStats?.sourceBreakdown?.abuseIPDB > 0 
                  ? "text-purple-600" 
                  : "text-gray-600"
                }>
                  {threatData?.globalStats?.sourceBreakdown?.abuseIPDB > 0 
                    ? 'Active' 
                    : 'Ready for Configuration'}
                </span>
              </div>
              <div className="flex items-center gap-1 px-2 py-1 bg-green-100 dark:bg-green-900/30 rounded">
                <div className={`w-2 h-2 rounded-full ${
                  threatData?.globalStats?.sourceBreakdown?.urlScan > 0 
                    ? 'bg-purple-500 animate-pulse' 
                    : 'bg-gray-400'
                }`}></div>
                <span>URLScan.io</span>
                <span className={threatData?.globalStats?.sourceBreakdown?.urlScan > 0 
                  ? "text-purple-600" 
                  : "text-gray-600"
                }>
                  {threatData?.globalStats?.sourceBreakdown?.urlScan > 0 
                    ? 'Active' 
                    : 'Ready for Configuration'}
                </span>
              </div>
              <div className="flex items-center gap-1 px-2 py-1 bg-purple-100 dark:bg-purple-900/30 rounded">
                <div className="w-2 h-2 bg-purple-500 rounded-full animate-pulse"></div>
                <span>AI Pattern Analysis</span>
                <span className="text-purple-600">Active</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Threat Map and Top Threats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Interactive World Threat Map */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="h-5 w-5 text-blue-600" />
              World Threat Map
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center h-96">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
                <span className="ml-3 text-slate-600">Loading threat map...</span>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Static world threat map */}
                <GoogleMaps 
                  threatData={regionalData.length > 0 ? regionalData.map(region => ({
                    lat: region.region === "North America" ? 39.8283 : 
                         region.region === "Europe" ? 54.5260 :
                         region.region === "Asia" ? 34.0479 :
                         region.region === "South America" ? -14.2350 :
                         region.region === "Africa" ? -0.0236 : 
                         region.region === "Oceania" ? -25.2744 : 20.0,
                    lng: region.region === "North America" ? -98.5795 : 
                         region.region === "Europe" ? 15.2551 :
                         region.region === "Asia" ? 100.6197 :
                         region.region === "South America" ? -51.9253 :
                         region.region === "Africa" ? 37.9062 : 
                         region.region === "Oceania" ? 133.7751 : 0.0,
                    region: region.region,
                    threats: region.threats,
                    severity: region.severity as 'low' | 'medium' | 'high',
                    details: [`${region.threats} active threats detected`, `Risk level: ${region.severity}`, 'Real-time monitoring active']
                  })) : [
                    // Default global threat data for demonstration
                    {
                      lat: 39.8283, lng: -98.5795, region: "North America", threats: 2, severity: 'high' as const,
                      details: ['2 active threats detected', 'Risk level: high', 'Real-time monitoring active']
                    },
                    {
                      lat: 54.5260, lng: 15.2551, region: "Europe", threats: 3, severity: 'medium' as const,
                      details: ['3 active threats detected', 'Risk level: medium', 'Real-time monitoring active']
                    },
                    {
                      lat: 34.0479, lng: 100.6197, region: "Asia", threats: 1, severity: 'high' as const,
                      details: ['1 active threat detected', 'Risk level: high', 'Real-time monitoring active']
                    },
                    {
                      lat: -14.2350, lng: -51.9253, region: "South America", threats: 1, severity: 'medium' as const,
                      details: ['1 active threat detected', 'Risk level: medium', 'Real-time monitoring active']
                    },
                    {
                      lat: -25.2744, lng: 133.7751, region: "Oceania", threats: 1, severity: 'high' as const,
                      details: ['1 active threat detected', 'Risk level: high', 'Real-time monitoring active']
                    }
                  ]}
                  height="450px"
                  className="w-full border-2 border-slate-200 dark:border-slate-700"
                />
                
                {/* Enhanced Regional Statistics */}
                <div className="grid grid-cols-3 lg:grid-cols-6 gap-3 mt-4">
                  {regionalData.map((region, index) => (
                    <div key={index} className="text-center p-3 bg-gradient-to-br from-white to-slate-50 dark:from-slate-700 dark:to-slate-800 rounded-lg border border-slate-200 dark:border-slate-600 hover:shadow-md transition-shadow">
                      <div className="text-sm font-bold text-slate-900 dark:text-white">{region.region}</div>
                      <div className={`text-lg font-bold mt-1 ${
                        region.severity === 'high' ? 'text-red-600' :
                        region.severity === 'medium' ? 'text-yellow-600' : 'text-purple-600'
                      }`}>
                        {region.threats}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-400">threats</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Top Threat Types */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-purple-600" />
              Top Threat Types
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {topThreatTypes.slice(0, 5).map((threat, index) => (
                <div key={index} className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    {getThreatIcon(threat.type)}
                    <span className="text-sm font-medium capitalize">{threat.type}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="text-sm text-slate-600">{threat.count}</div>
                    <div className="w-16 bg-slate-200 rounded-full h-1.5">
                      <div 
                        className="h-1.5 bg-purple-500 rounded-full"
                        style={{ width: `${topThreatTypes.length > 0 ? (threat.count / Math.max(...topThreatTypes.map(t => t.count))) * 100 : 0}%` }}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Threat Intelligence Feed */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className={`h-5 w-5 text-red-600 transition-transform duration-300 ${liveFeedActive ? 'animate-pulse' : ''}`} />
            Live Threat Intelligence Feed
            <div className={`w-2 h-2 rounded-full ml-2 ${liveFeedActive ? 'bg-red-500 animate-ping' : 'bg-slate-400'}`}></div>
          </CardTitle>
          
          {/* Search and Filter Controls */}
          <div className="flex gap-4 mt-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by IP, country, or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="px-4 py-2 border rounded-md bg-white dark:bg-slate-800"
            >
              <option value="all">All Types</option>
              <option value="malware">Malware</option>
              <option value="phishing">Phishing</option>
              <option value="botnet">Botnet</option>
              <option value="ddos">DDoS</option>
              <option value="vulnerability">Vulnerability</option>
            </select>
          </div>
        </CardHeader>
        
        <CardContent>
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600"></div>
              <span className="ml-3 text-slate-600">Loading threat intelligence...</span>
            </div>
          ) : (
            <>
              <div className="space-y-3 max-h-96 overflow-y-auto">
                {filteredThreats.slice(0, 20).map((threat, index) => (
                  <div
                    key={threat.id}
                    className={`flex items-center justify-between p-4 border rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/50 transition-all duration-300 cursor-pointer transform hover:scale-[1.02] hover:shadow-md ${
                      liveFeedActive && index < 3 ? 'animate-pulse border-emerald-300 bg-purple-50/50' : ''
                    }`}
                    onClick={() => setSelectedThreat(threat)}
                    style={{ 
                      animationDelay: liveFeedActive ? `${index * 100}ms` : '0ms',
                      borderLeft: liveFeedActive && index < 3 ? '4px solid #10b981' : 'none'
                    }}
                  >
                    <div className="flex items-center gap-4 flex-1">
                      {getThreatIcon(threat.type)}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-medium text-sm">{threat.sourceIp}</span>
                          <Badge variant="outline" className="text-xs capitalize">
                            {threat.type}
                          </Badge>
                          <Badge className={`text-xs border ${getSeverityColor(threat.severity)}`}>
                            {threat.severity}
                          </Badge>
                        </div>
                        <div className="text-sm text-slate-600 dark:text-slate-400">
                          {threat.description}
                        </div>
                        <div className="flex items-center gap-4 mt-1 text-xs text-slate-500">
                          <span className="flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            {threat.city}, {threat.country}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {new Date(threat.timestamp).toLocaleTimeString()}
                          </span>
                          <span>Confidence: {threat.confidence}%</span>
                          {threat.detectionSource && (
                            <Badge 
                              variant="outline" 
                              className={`text-xs ${
                                threat.detectionSource === 'VirusTotal' ? 'border-blue-300 text-blue-700' :
                                threat.detectionSource === 'AbuseIPDB' ? 'border-red-300 text-red-700' :
                                threat.detectionSource === 'URLScan.io' ? 'border-green-300 text-green-700' :
                                'border-purple-300 text-purple-700'
                              }`}
                            >
                              {threat.detectionSource === 'Multiple' ? '🔍 AI Analysis' : 
                               threat.detectionSource === 'VirusTotal' ? '🛡️ VirusTotal' :
                               threat.detectionSource === 'AbuseIPDB' ? '⚠️ AbuseIPDB' :
                               threat.detectionSource === 'URLScan.io' ? '🔎 URLScan' :
                               threat.detectionSource}
                            </Badge>
                          )}
                          {threat.virusTotalScore !== undefined && (
                            <span className="text-red-600 font-medium">
                              VT: {threat.virusTotalScore}/70+
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button variant="ghost" size="sm">
                      <Eye className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
              
              {filteredThreats.length > 20 && (
                <div className="text-center pt-4 border-t">
                  <Button variant="outline">
                    Load More Threats ({filteredThreats.length - 20} remaining)
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Threat Details Modal */}
      {selectedThreat && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <Card className="max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                {getThreatIcon(selectedThreat.type)}
                Threat Details - {selectedThreat.sourceIp}
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setSelectedThreat(null)}
                  className="ml-auto"
                >
                  ×
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-slate-600">Source IP</label>
                  <div className="font-mono text-sm bg-slate-100 p-2 rounded">
                    {selectedThreat.sourceIp}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Threat Type</label>
                  <div className="flex items-center gap-2 mt-1">
                    {getThreatIcon(selectedThreat.type)}
                    <span className="capitalize">{selectedThreat.type}</span>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Severity</label>
                  <div className="mt-1">
                    <Badge className={`border ${getSeverityColor(selectedThreat.severity)}`}>
                      {selectedThreat.severity}
                    </Badge>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-600">Confidence</label>
                  <div className="text-sm font-medium">{selectedThreat.confidence}%</div>
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-600">Location</label>
                <div className="text-sm">
                  {selectedThreat.city}, {selectedThreat.country} ({selectedThreat.region})
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-600">Attack Vector</label>
                <div className="text-sm">{selectedThreat.attackVector}</div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-600">Target Domains</label>
                <div className="space-y-1">
                  {selectedThreat.targetDomains.map((domain, i) => (
                    <div key={i} className="text-sm font-mono bg-slate-100 p-1 rounded">
                      {domain}
                    </div>
                  ))}
                </div>
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-600">Description</label>
                <div className="text-sm">{selectedThreat.description}</div>
              </div>

              {/* Enhanced threat intelligence fields */}
              <div className="bg-slate-50 dark:bg-slate-800 p-4 rounded-lg space-y-3">
                <h4 className="font-medium text-slate-800 dark:text-slate-200">Intelligence Source Details</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-600">Detection Source</label>
                    <Badge variant="outline" className="mt-1">
                      {selectedThreat.detectionSource || 'Multiple'}
                    </Badge>
                  </div>
                  
                  {selectedThreat.abuseConfidence && (
                    <div>
                      <label className="text-sm font-medium text-slate-600">AbuseIPDB Confidence</label>
                      <div className="text-sm font-medium text-red-600">
                        {selectedThreat.abuseConfidence}%
                      </div>
                    </div>
                  )}

                  {selectedThreat.virusTotalScore && (
                    <div>
                      <label className="text-sm font-medium text-slate-600">VirusTotal Detections</label>
                      <div className="text-sm font-medium text-orange-600">
                        {selectedThreat.virusTotalScore} engines
                      </div>
                    </div>
                  )}

                  {selectedThreat.urlScanVerdict && (
                    <div>
                      <label className="text-sm font-medium text-slate-600">URLScan Verdict</label>
                      <Badge className={selectedThreat.urlScanVerdict === 'malicious' ? 'bg-red-100 text-red-800' : 'bg-yellow-100 text-yellow-800'}>
                        {selectedThreat.urlScanVerdict}
                      </Badge>
                    </div>
                  )}

                  {selectedThreat.isp && (
                    <div>
                      <label className="text-sm font-medium text-slate-600">ISP</label>
                      <div className="text-sm">{selectedThreat.isp}</div>
                    </div>
                  )}

                  {selectedThreat.reports && (
                    <div>
                      <label className="text-sm font-medium text-slate-600">Reports</label>
                      <div className="text-sm font-medium">{selectedThreat.reports} reports</div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex justify-between pt-4 border-t">
                <Button variant="outline" size="sm">
                  <ExternalLink className="h-4 w-4 mr-2" />
                  WHOIS Lookup
                </Button>
                <Button onClick={() => setSelectedThreat(null)}>
                  Close
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}